<template>
  <v-container fluid class="purchase-order-list-page pa-0">
    <v-card :loading="loading" outlined class="purchase-order-list-card">
      <div class="purchase-order-header">
        <div class="purchase-order-title-wrap">
          <v-btn
            v-if="!VendorId"
            icon
            small
            class="mr-2"
            @click="$router.go(-1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <div>
            <div class="page-title">Purchase Order List</div>
            <div class="page-subtitle">
              {{ Invoices.length }} order{{ Invoices.length === 1 ? "" : "s" }} found
            </div>
          </div>
        </div>

        <v-btn
          v-if="checkRightStatus(4)"
          small
          rounded
          color="primary"
          dark
          @click="add"
        >
          <v-icon left small>mdi-plus</v-icon>
          New
        </v-btn>
      </div>

      <div class="purchase-order-filter-bar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          autofocus
          dense
          label="Search order, vendor"
          single-line
          hide-details
          clearable
          class="purchase-order-search"
        />

        <v-spacer />

        <DateTimePicker
          v-if="!showAll"
          defaultRange="since60DaysAgo"
          :range="true"
          :hideTime="true"
          v-model="dateTime"
          @submit="getList()"
        />
      </div>

      <v-card-text class="pa-4">
        <div class="summary-row mb-4">
          <div class="summary-box">
            <div class="summary-label">List Total</div>
            <div class="summary-value">{{ total | currency }}</div>
          </div>

          <div class="summary-box">
            <div class="summary-label">Result Total</div>
            <div class="summary-value">{{ fullTotal | currency }}</div>
          </div>
        </div>

        <v-data-table
          :mobile-breakpoint="0"
          :headers="headers"
          :items="Invoices"
          :search="search"
          :loading="loading"
          @current-items="updateTotal"
          :sort-by="sortBy"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          class="purchase-order-table"
        >
          <template v-slot:item="{ item }">
            <tr class="pointer purchase-order-row">
              <td class="text-left" @click="viewOrder(item)">
                {{ item.date | moment("MM/DD/YYYY hh:mm a") }}
              </td>

              <td class="text-left font-weight-bold" @click="viewOrder(item)">
                {{ item.localId }}
              </td>

              <td
                v-if="!VendorId"
                class="text-left"
                @click="viewOrder(item)"
              >
                {{ item.Vendor ? item.Vendor.name : "Unknown Vendor" }}
              </td>

              <td class="text-right font-weight-bold" @click="viewOrder(item)">
                {{ item.total | currency }}
              </td>

              <td class="text-left">
                <v-chip
                  v-if="item.completeDate"
                  color="green lighten-5"
                  small
                  text-color="green darken-3"
                  class="font-weight-bold"
                >
                  Completed
                </v-chip>

                <v-chip
                  v-else
                  color="orange lighten-5"
                  small
                  text-color="orange darken-3"
                  class="font-weight-bold"
                >
                  Pending
                </v-chip>
              </td>

              <td class="justify-end align-center text-right">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense class="menu-button">
                    <v-list-item
                      v-if="!VendorId && item.VendorId"
                      @click="viewProfile(item)"
                    >
                      <v-list-item-title>Profile</v-list-item-title>
                    </v-list-item>

                    <OrderStatus :order="item" @submit="getList()" />

                    <v-divider v-if="!VendorId" />

                    <v-list-item @click="viewOrder(item)">
                      <v-list-item-title>View Order</v-list-item-title>
                    </v-list-item>

                    <v-divider />

                    <v-list-item @click="editOrder(item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteOrder(item)">
                      <v-list-item-title class="red--text">
                        Delete
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <v-alert slot="no-results" :value="true" color="error" icon="warning">
            Your search for "{{ search }}" found no results.
          </v-alert>

          <template v-slot:body.append="{ headers }" v-if="Invoices.length > 0">
            <tr class="table-total-row">
              <td
                :colspan="headers.length - 3"
                class="text-right font-weight-bold"
              >
                List Total
              </td>

              <td class="text-right font-weight-bold">
                {{ total | currency }}
              </td>

              <td colspan="2"></td>
            </tr>

            <tr class="table-result-row">
              <td
                :colspan="headers.length - 3"
                class="text-right font-weight-bold"
              >
                Result Total
              </td>

              <td class="text-right font-weight-bold">
                {{ fullTotal | currency }}
              </td>

              <td colspan="2"></td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import purchaseOrderService from "../service";
import { mapGetters } from "vuex";
import Hashids from "hashids";

const DateTimePicker = () => import("@/components/DateTimePicker");
const OrderStatus = () =>
  import("@/modules/Inventory/PurchaseOrder/components/OrderStatus");

const hashids = new Hashids();

export default {
  name: "purchaseOrder-list",

  props: {
    VendorId: {
      default: null,
    },
    showAll: {
      default: false,
    },
    due: {
      default: false,
    },
  },

  components: {
    DateTimePicker,
    OrderStatus,
  },

  data() {
    return {
      fullTotal: 0,
      total: 0,
      dateTime: {
        start: this.$moment()
          .startOf("day")
          .subtract(60, "days")
          .toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      count: null,
      headers: [],
      Invoices: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "date",
      pageCount: 0,
      itemsPerPage: 10,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),
  },

  watch: {
    VendorId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
  },

  created() {
    this.loadHeader();
    document.title = "smplsale - Purchase Order List";
  },

  methods: {
    updateTotal(val) {
      this.total = val.reduce((sum, row) => {
        return sum + Number(row.total || 0);
      }, 0);
    },

    add() {
      if (this.VendorId) {
        this.$router.push({
          path: `/inventory/purchaseOrder/create?VendorId=${this.VendorId}`,
        });
        return;
      }

      this.$router.push({ path: "/inventory/purchaseOrder/create" });
    },

    viewProfile(val) {
      if (!val.VendorId) {
        return;
      }

      this.$router.push({ path: `/inventory/vendor/${val.VendorId}/profile` });
    },

    viewOrder(purchaseOrder) {
      const hashId = hashids.encode(purchaseOrder.id);

      const routeData = this.$router.resolve({
        path: `/global/purchaseOrder/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    loadHeader() {
      this.headers = [
        {
          text: this.$t("labels.date"),
          align: "left",
          value: "date",
          sortable: true,
        },
        {
          text: "Order Number",
          align: "left",
          value: "localId",
          sortable: true,
        },
      ];

      if (!this.VendorId) {
        this.headers.push({
          text: "Vendor Name",
          value: "Vendor.name",
          align: "left",
          sortable: true,
        });
      }

      this.headers.push(
        {
          text: "Total",
          value: "total",
          align: "right",
          sortable: true,
        },
        {
          text: "Status",
          value: "status",
          align: "left",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        }
      );
    },

    async getList() {
      this.loading = true;

      const where = {};

      if (!this.showAll) {
        where.start = this.dateTime.start;
        where.end = this.dateTime.end;
      }

      if (this.VendorId) {
        where.VendorId = this.VendorId;
      }

      try {
        const response = await purchaseOrderService.getAll(where);

        this.Invoices = response.data || [];
        this.fullTotal = this.Invoices.reduce((sum, row) => {
          return sum + Number(row.total || 0);
        }, 0);

        this.count = response.count;

        return response;
      } catch (err) {
        console.error("purchase order getList failed", err);
        this.Invoices = [];
        this.total = 0;
        this.fullTotal = 0;
      } finally {
        this.loading = false;
      }
    },

    editOrder(purchaseOrder) {
      this.$router.push({
        path: `/inventory/purchaseOrder/${purchaseOrder.id}/edit`,
      });
    },

    deleteOrder(purchaseOrder) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          purchaseOrderService.delete(purchaseOrder.id).then((response) => {
            if (response.data) {
              this.$swal(
                "Deleted!",
                "Purchase Order has been deleted.",
                "success"
              );

              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.purchase-order-list-page {
  background: #f8fafc;
}

.purchase-order-list-card {
  border-radius: 20px;
  background: #ffffff;
}

.purchase-order-header {
  padding: 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.purchase-order-title-wrap {
  display: flex;
  align-items: center;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
}

.page-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.purchase-order-filter-bar {
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.purchase-order-search {
  max-width: 340px;
  min-width: 260px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 220px));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.purchase-order-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.purchase-order-row:hover {
  background: #f8fafc;
}

.table-total-row {
  background: #f8fafc;
}

.table-result-row {
  background: #eef2ff;
}

@media (max-width: 600px) {
  .purchase-order-search {
    max-width: 100%;
    min-width: 100%;
  }

  .summary-row {
    grid-template-columns: 1fr;
  }
}
</style>